/**
 * Theme system for My Fate, My Love (MFML) RP web app
 * 12 character-named dark theme variants
 */

/**
 * Default theme tokens — "Velvet & Gold" (the default 'dark' experience).
 * Deep wine-black grounds, blood-red primary with candle-glow, antique gold
 * accents. Reads like a leather-bound romance novel. Used as a standalone
 * fallback AND mirrored as THEMES.dark below (the default themeId).
 */
export const THEME = {
  bgDeepest: '#050406',
  bgBase: '#100c10',
  bgSecondary: '#181219',
  bgTertiary: '#201922',
  bgInput: '#0a070a',
  bgHover: 'rgba(212, 175, 55, 0.06)',
  borderDark: '#251c26',
  borderMedium: '#372b39',
  borderLight: '#4e3f52',
  textPrimary: '#f5efe4',
  textSecondary: '#d2c6b6',
  textMuted: '#9d9182',
  textFaint: '#6c6253',
  accentPrimary: '#2a5c9e',
  accentPrimaryDark: '#1c4070',
  accentPrimaryBg: '#2a5c9e22',
  accentSecondary: '#b89030',
  accentSecondaryLight: '#e8c969',
  accentSuccess: '#2f9e6f',
  accentWarning: '#d9a635',
  accentDanger: '#c43050',
  accentRomance: '#d98aa6',
  accentGold: '#d4af37',
  online: '#2f9e6f',
  offline: '#6c6253',
  typing: '#e8c969',
};

/**
 * 12 character-named theme variants
 */
export const THEMES = {

  // ── Velvet & Gold — the DEFAULT 'dark' experience ────────────────────────
  // Wine-black grounds, blood-red primary with candle-glow, antique gold.
  // themeId defaults to 'dark' (ThemeContext) and resolves here.
  dark: {
    bgDeepest: '#050406',
    bgBase: '#100c10',
    bgSecondary: '#181219',
    bgTertiary: '#201922',
    bgInput: '#0a070a',
    bgHover: 'rgba(212, 175, 55, 0.06)',
    borderDark: '#251c26',
    borderMedium: '#372b39',
    borderLight: '#4e3f52',
    textPrimary: '#f5efe4',
    textSecondary: '#d2c6b6',
    textMuted: '#9d9182',
    textFaint: '#6c6253',
    accentPrimary: '#2a5c9e',
    accentPrimaryDark: '#1c4070',
    accentPrimaryBg: '#2a5c9e22',
    accentSecondary: '#b89030',
    accentSecondaryLight: '#e8c969',
    accentSuccess: '#2f9e6f',
    accentWarning: '#d9a635',
    accentDanger: '#c43050',
    accentRomance: '#d98aa6',
    accentGold: '#d4af37',
    online: '#2f9e6f',
    offline: '#6c6253',
    typing: '#e8c969',
  },

  // ── Atropos — cold fate, dark silver ─────────────────────────────────────
  atropos: {
    bgDeepest: '#060607',
    bgBase: '#0e0e12',
    bgSecondary: '#181820',
    bgTertiary: '#202028',
    bgInput: '#0b0b0f',
    bgHover: 'rgba(200, 200, 230, 0.06)',
    borderDark: '#1c1c24',
    borderMedium: '#2c2c38',
    borderLight: '#3c3c50',
    textPrimary: '#e4e4f0',
    textSecondary: '#b8b8cc',
    textMuted: '#888898',
    textFaint: '#5c5c70',
    accentPrimary: '#c0c0e0',
    accentPrimaryDark: '#9090b8',
    accentPrimaryBg: '#c0c0e022',
    accentSecondary: '#8888b0',
    accentSecondaryLight: '#b0b0d0',
    accentSuccess: '#45c585',
    accentWarning: '#c8a030',
    accentDanger: '#cc4040',
    accentRomance: '#b03878',
    accentGold: '#a89060',
    online: '#45c585',
    offline: '#5c5c70',
    typing: '#c0c0e0',
  },

  // ── Lachesis — mystic measurer, deep purple/lavender ─────────────────────
  lachesis: {
    bgDeepest: '#08040f',
    bgBase: '#100a1e',
    bgSecondary: '#1a1030',
    bgTertiary: '#24163e',
    bgInput: '#0c081a',
    bgHover: 'rgba(180, 120, 255, 0.08)',
    borderDark: '#1c1430',
    borderMedium: '#2e2050',
    borderLight: '#402e68',
    textPrimary: '#e8d8ff',
    textSecondary: '#c4a8e8',
    textMuted: '#9070b0',
    textFaint: '#685088',
    accentPrimary: '#c890ff',
    accentPrimaryDark: '#9060d8',
    accentPrimaryBg: '#c890ff22',
    accentSecondary: '#a060e0',
    accentSecondaryLight: '#d0a0ff',
    accentSuccess: '#50d898',
    accentWarning: '#ffcc55',
    accentDanger: '#ff5080',
    accentRomance: '#d060a8',
    accentGold: '#e8c050',
    online: '#50d898',
    offline: '#685088',
    typing: '#c890ff',
  },

  // ── Kya — ocean siren, deep midnight blue/cyan ────────────────────────────
  kya: {
    bgDeepest: '#020810',
    bgBase: '#050f20',
    bgSecondary: '#0a1830',
    bgTertiary: '#101e38',
    bgInput: '#071220',
    bgHover: 'rgba(80, 190, 255, 0.08)',
    borderDark: '#0a1428',
    borderMedium: '#142033',
    borderLight: '#1e3048',
    textPrimary: '#c8e8ff',
    textSecondary: '#90c8f0',
    textMuted: '#5898c0',
    textFaint: '#407090',
    accentPrimary: '#58d0ff',
    accentPrimaryDark: '#30a8e0',
    accentPrimaryBg: '#58d0ff22',
    accentSecondary: '#3088ff',
    accentSecondaryLight: '#68b8ff',
    accentSuccess: '#40d890',
    accentWarning: '#ffcc50',
    accentDanger: '#ff5070',
    accentRomance: '#c060f8',
    accentGold: '#ffd060',
    online: '#40d890',
    offline: '#407090',
    typing: '#58d0ff',
  },

  // ── Evie — vampire queen, dark blood-crimson/rose ─────────────────────────
  evie: {
    bgDeepest: '#0e0005',
    bgBase: '#180008',
    bgSecondary: '#250010',
    bgTertiary: '#301018',
    bgInput: '#140006',
    bgHover: 'rgba(220, 0, 60, 0.09)',
    borderDark: '#220010',
    borderMedium: '#3a0820',
    borderLight: '#560e30',
    textPrimary: '#ffd8e0',
    textSecondary: '#e090a8',
    textMuted: '#b05870',
    textFaint: '#783848',
    accentPrimary: '#ff2060',
    accentPrimaryDark: '#cc0038',
    accentPrimaryBg: '#ff206022',
    accentSecondary: '#e00030',
    accentSecondaryLight: '#ff5080',
    accentSuccess: '#40d090',
    accentWarning: '#ffaa44',
    accentDanger: '#ff1040',
    accentRomance: '#e00030',
    accentGold: '#cc8844',
    online: '#40d090',
    offline: '#783848',
    typing: '#ff2060',
  },

  // ── Kieran — fae king, midnight forest with ancient gold ──────────────────
  kieran: {
    // Literally a gold-themed character — the ornament IS the point.
    goldIsIntentional: true,
    bgDeepest: '#030500',
    bgBase: '#080d00',
    bgSecondary: '#101500',
    bgTertiary: '#181e04',
    bgInput: '#060800',
    bgHover: 'rgba(200, 160, 0, 0.08)',
    borderDark: '#161a04',
    borderMedium: '#282e0a',
    borderLight: '#3c4410',
    textPrimary: '#f0e8c0',
    textSecondary: '#d0c090',
    textMuted: '#a09060',
    textFaint: '#706840',
    accentPrimary: '#d4a82a',
    accentPrimaryDark: '#a88020',
    accentPrimaryBg: '#d4a82a22',
    accentSecondary: '#a07000',
    accentSecondaryLight: '#e0c050',
    accentSuccess: '#4ac880',
    accentWarning: '#f0aa20',
    accentDanger: '#cc4040',
    accentRomance: '#d04080',
    accentGold: '#d4a82a',
    online: '#4ac880',
    offline: '#706840',
    typing: '#d4a82a',
  },

  // ── Lance — dark noble Ralidanti, deep forest emerald ────────────────────
  lance: {
    bgDeepest: '#020806',
    bgBase: '#060f08',
    bgSecondary: '#0e1a10',
    bgTertiary: '#152016',
    bgInput: '#080e0a',
    bgHover: 'rgba(40, 180, 90, 0.08)',
    borderDark: '#102014',
    borderMedium: '#1c3220',
    borderLight: '#2a4830',
    textPrimary: '#c8f0d0',
    textSecondary: '#88c898',
    textMuted: '#508860',
    textFaint: '#386840',
    accentPrimary: '#2ed870',
    accentPrimaryDark: '#20a850',
    accentPrimaryBg: '#2ed87022',
    accentSecondary: '#189048',
    accentSecondaryLight: '#50f088',
    accentSuccess: '#2ed870',
    accentWarning: '#d8c040',
    accentDanger: '#e04040',
    accentRomance: '#c04080',
    accentGold: '#c8a840',
    online: '#2ed870',
    offline: '#386840',
    typing: '#2ed870',
  },

  // ── Cruz — alpha wolf, warm dark earth/sage ───────────────────────────────
  cruz: {
    bgDeepest: '#060400',
    bgBase: '#0e0a02',
    bgSecondary: '#181204',
    bgTertiary: '#201808',
    bgInput: '#0a0802',
    bgHover: 'rgba(140, 180, 80, 0.08)',
    borderDark: '#1c1608',
    borderMedium: '#2e2810',
    borderLight: '#443c1c',
    textPrimary: '#e8e0c8',
    textSecondary: '#c0b898',
    textMuted: '#908868',
    textFaint: '#686050',
    accentPrimary: '#9ab860',
    accentPrimaryDark: '#709040',
    accentPrimaryBg: '#9ab86022',
    accentSecondary: '#6a8830',
    accentSecondaryLight: '#b8d880',
    accentSuccess: '#68c870',
    accentWarning: '#d8a840',
    accentDanger: '#d04040',
    accentRomance: '#c06080',
    accentGold: '#c8a040',
    online: '#68c870',
    offline: '#686050',
    typing: '#9ab860',
  },

  // ── Sebby — siren king, deep navy indigo ─────────────────────────────────
  sebby: {
    bgDeepest: '#020410',
    bgBase: '#060818',
    bgSecondary: '#0c1028',
    bgTertiary: '#121838',
    bgInput: '#080c1c',
    bgHover: 'rgba(80, 80, 255, 0.09)',
    borderDark: '#101828',
    borderMedium: '#1c2840',
    borderLight: '#2a3858',
    textPrimary: '#d0d8ff',
    textSecondary: '#9898e0',
    textMuted: '#6870b8',
    textFaint: '#485090',
    accentPrimary: '#6060ff',
    accentPrimaryDark: '#4040cc',
    accentPrimaryBg: '#6060ff22',
    accentSecondary: '#4040dd',
    accentSecondaryLight: '#8888ff',
    accentSuccess: '#40d890',
    accentWarning: '#ffcc44',
    accentDanger: '#ff5060',
    accentRomance: '#c060e0',
    accentGold: '#d0a840',
    online: '#40d890',
    offline: '#485090',
    typing: '#6060ff',
  },

  // ── Alistair — ancient vampire, dark wine with amber gold ────────────────
  alistair: {
    // Literally a gold-themed character — the ornament IS the point.
    goldIsIntentional: true,
    bgDeepest: '#080204',
    bgBase: '#100608',
    bgSecondary: '#1a0c0e',
    bgTertiary: '#221216',
    bgInput: '#0c0408',
    bgHover: 'rgba(180, 100, 20, 0.08)',
    borderDark: '#1e1010',
    borderMedium: '#301820',
    borderLight: '#482430',
    textPrimary: '#f0ddc0',
    textSecondary: '#d0b088',
    textMuted: '#a07858',
    textFaint: '#705040',
    accentPrimary: '#d4902a',
    accentPrimaryDark: '#a86820',
    accentPrimaryBg: '#d4902a22',
    accentSecondary: '#c07018',
    accentSecondaryLight: '#f0b050',
    accentSuccess: '#48c880',
    accentWarning: '#f0a020',
    accentDanger: '#e04040',
    accentRomance: '#d04060',
    accentGold: '#d4902a',
    online: '#48c880',
    offline: '#705040',
    typing: '#d4902a',
  },

  // ── Eva — shy wolf, deep dark with warm mauve pink ────────────────────────
  eva: {
    bgDeepest: '#0e0608',
    bgBase: '#180e12',
    bgSecondary: '#251820',
    bgTertiary: '#30202c',
    bgInput: '#140c10',
    bgHover: 'rgba(255, 100, 160, 0.08)',
    borderDark: '#231620',
    borderMedium: '#382030',
    borderLight: '#503048',
    textPrimary: '#f8d8e8',
    textSecondary: '#d0a0c0',
    textMuted: '#a07090',
    textFaint: '#784860',
    accentPrimary: '#f080a8',
    accentPrimaryDark: '#c85888',
    accentPrimaryBg: '#f080a822',
    accentSecondary: '#d06090',
    accentSecondaryLight: '#ff98c0',
    accentSuccess: '#58d898',
    accentWarning: '#ffcc66',
    accentDanger: '#ff5070',
    accentRomance: '#d06090',
    accentGold: '#f0c070',
    online: '#58d898',
    offline: '#784860',
    typing: '#f080a8',
  },

  // ── Brax & Vaughn — two wolves, deep dark with vivid fuchsia ─────────────
  braxvaughn: {
    bgDeepest: '#0c0010',
    bgBase: '#160020',
    bgSecondary: '#220030',
    bgTertiary: '#2e0040',
    bgInput: '#100018',
    bgHover: 'rgba(200, 40, 220, 0.09)',
    borderDark: '#240038',
    borderMedium: '#380058',
    borderLight: '#520078',
    textPrimary: '#f0d0ff',
    textSecondary: '#d098e8',
    textMuted: '#a060c0',
    textFaint: '#704090',
    accentPrimary: '#e040c0',
    accentPrimaryDark: '#b01898',
    accentPrimaryBg: '#e040c022',
    accentSecondary: '#c020a0',
    accentSecondaryLight: '#f070d8',
    accentSuccess: '#50d898',
    accentWarning: '#ffcc55',
    accentDanger: '#ff4060',
    accentRomance: '#c020a0',
    accentGold: '#f0c060',
    online: '#50d898',
    offline: '#704090',
    typing: '#e040c0',
  },

  // ── Agnar & Agmund — norse gods, deep cold nordic blue ───────────────────
  agnaragmund: {
    bgDeepest: '#020810',
    bgBase: '#060e1c',
    bgSecondary: '#0c162c',
    bgTertiary: '#121e3c',
    bgInput: '#08101e',
    bgHover: 'rgba(30, 100, 200, 0.09)',
    borderDark: '#101a30',
    borderMedium: '#1c2c4a',
    borderLight: '#2a3e62',
    textPrimary: '#d0e4ff',
    textSecondary: '#90b4e0',
    textMuted: '#6088b8',
    textFaint: '#406090',
    accentPrimary: '#3a88e8',
    accentPrimaryDark: '#2060c0',
    accentPrimaryBg: '#3a88e822',
    accentSecondary: '#1866c0',
    accentSecondaryLight: '#60a8ff',
    accentSuccess: '#40d890',
    accentWarning: '#f0c040',
    accentDanger: '#f04040',
    accentRomance: '#d040a0',
    accentGold: '#d0b040',
    online: '#40d890',
    offline: '#406090',
    typing: '#3a88e8',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  //  LIGHT THEMES — luminous, quiet-luxury. The whole app has always been
  //  near-black; these are the first genuinely LIGHT experiences. `light: true`
  //  makes buildTokens + ThemeContext + index.css adapt (softer glows, warm
  //  vignette instead of a dark one, tone-on-tone ornament).
  // ═══════════════════════════════════════════════════════════════════════════

  // ── Porcelain Rose — warm ivory, blush rose, antique gold. Romance, luxe. ──
  porcelain: {
    light: true,
    bgDeepest: '#f3ebe3',   // warm ivory "page"
    bgBase: '#fbf5ef',      // card
    bgSecondary: '#fffdfb',  // raised surface (glass reads off this)
    bgTertiary: '#f6ede5',
    bgInput: '#fffdfb',
    bgHover: 'rgba(194, 91, 124, 0.08)',
    borderDark: '#eaddd1',
    borderMedium: '#dcc8b8',
    borderLight: '#cbb29e',
    textPrimary: '#3a2c28',   // warm near-black — AA on the ivory
    textSecondary: '#6a544c',
    textMuted: '#9a8577',
    textFaint: '#b7a190',
    accentPrimary: '#c25b7c',  // blush rose
    accentPrimaryDark: '#a2445f',
    accentPrimaryBg: '#c25b7c22',
    accentSecondary: '#b0872b',
    accentSecondaryLight: '#d8b45a',
    accentSuccess: '#3f9d6e',
    accentWarning: '#c2892a',
    accentDanger: '#c2455d',
    accentRomance: '#cf7194',
    accentGold: '#a9842b',   // darker gold so it reads on light
    online: '#3f9d6e',
    offline: '#b7a190',
    typing: '#c25b7c',
  },

  // ── Daybreak — airy blue-white, soft cornflower, warm gold. Calm, clear. ──
  daybreak: {
    light: true,
    bgDeepest: '#eaf0f7',   // soft blue-white
    bgBase: '#f7fafd',
    bgSecondary: '#ffffff',
    bgTertiary: '#e9f0f7',
    bgInput: '#ffffff',
    bgHover: 'rgba(58, 122, 190, 0.08)',
    borderDark: '#dde6f0',
    borderMedium: '#c7d5e6',
    borderLight: '#abbdd4',
    textPrimary: '#20303f',
    textSecondary: '#48596b',
    textMuted: '#7889a0',
    textFaint: '#a4b4c8',
    accentPrimary: '#3a7abe',  // cornflower
    accentPrimaryDark: '#295f9d',
    accentPrimaryBg: '#3a7abe22',
    accentSecondary: '#bd922f',
    accentSecondaryLight: '#dcb85a',
    accentSuccess: '#3f9d6e',
    accentWarning: '#c99a2c',
    accentDanger: '#cf4f63',
    accentRomance: '#cf7194',
    accentGold: '#b28c2c',
    online: '#3f9d6e',
    offline: '#a4b4c8',
    typing: '#3a7abe',
  },
};

/**
 * Font scale for typography
 * Sizes in pixels
 */
export const TYPE_SCALE = {
  xs: 10,
  sm: 12,
  base: 14,
  md: 16,
  lg: 20,
  xl: 24,
  xxl: 32,
  display: 42,
};

// (A SPACING scale lived here until 2026-08-12. Its last three importers never
// used it — the design system's --ds-* space tokens from buildTokens() are the
// real spacing source — so when those imports were cleaned up the export went
// with them.)

/**
 * Species-specific colors for character display
 * Maps species name to color hex code
 */
export const SPECIES_COLORS = {
  Siren: '#3498db',
  Vampire: '#e74c3c',
  Werewolf: '#ff9800',
  Fae: '#2ecc71',
  Fairy: '#2ecc71',
  God: '#9b59b6',
  Goddess: '#9b59b6',
  Demon: '#f04747',
  Witch: '#1abc9c',
  Human: '#95a5a6',
  Hybrid: '#e67e22',
  Ghost: '#607d8b',
  Angel: '#ffd54f',
  Phoenix: '#ff5722',
  Dragon: '#8bc34a',
  Elf: '#00bcd4',
};

/* ═══════════════════════════════════════════════════════════════════════════
   Design-system token layer — v5 "Candlelit Glass"
   Single source of truth for the app's visual language. Every value here is
   derived from the ACTIVE theme palette, so the user's theme pick (or custom
   accent) re-skins the entire app — surfaces, glass, glows, hairlines, focus.
   ThemeContext publishes these as --ds-* CSS custom properties on :root.
   ═══════════════════════════════════════════════════════════════════════════ */

// Append an 8-bit alpha to #rrggbb / #rgb (iOS-15-safe; no color-mix()).
const wa = (color, aa) => {
  if (typeof color !== 'string' || color[0] !== '#') return color;
  if (color.length === 4) {
    const r = color[1], g = color[2], b = color[3];
    return `#${r}${r}${g}${g}${b}${b}${aa}`;
  }
  if (color.length === 7) return `${color}${aa}`;
  return color;
};

// Multiply a hex color toward black (f < 1) — for the "deeper" accent shade.
const shade = (hex, f) => {
  if (typeof hex !== 'string' || hex.length !== 7 || hex[0] !== '#') return hex;
  const n = parseInt(hex.slice(1), 16);
  const r = Math.round(((n >> 16) & 255) * f);
  const g = Math.round(((n >> 8) & 255) * f);
  const b = Math.round((n & 255) * f);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

// Alpha ladders: every translucent accent/gold step used by the app's inline
// styles (migrated to var(--ds-accent-aXX) / var(--ds-gold-aXX) with literal
// fallbacks). Generated by scripts/migrate-tokens.py — keep in sync.
export const ACCENT_ALPHA_STEPS = ['00', '0a', '0d', '0f', '10', '11', '12', '14', '15', '18', '1a', '1f', '20', '22', '24', '26', '29', '2e', '33', '40', '44', '47', '4c', '55', '59', '66', '80', '88', '99'];
export const GOLD_ALPHA_STEPS = ['0a', '0f', '11', '12', '14', '15', '18', '1a', '1f', '22', '24', '26', '2e', '33', '38', '40', '44', '47', '4c', '52', '55', '59', '66', '73', '80', '88', '8c', '99', 'b2'];

// WCAG-ish relative luminance → pick ink that actually READS on a given bg.
// The 2026-07-27 "buttons are unreadable" class: gold text on accent
// gradients works on the dark themes but dies on light ones (blush rose,
// porcelain), because token PAIRS were never contrast-checked per theme.
// Every on-color below is computed, so every theme fixes itself.
function lum(hex) {
  const h = String(hex || '').replace('#', '');
  if (h.length < 6) return 0;
  const c = [0, 2, 4].map(i => {
    let v = parseInt(h.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}
export function readableOn(bg) {
  return lum(bg) > 0.42 ? '#241712' : '#fff8f2';
}

function contrast(a, b) {
  const la = lum(a), lb = lum(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

// Same idea as readableOn, but KEEPS the hue instead of collapsing to ink.
// Walks a color's lightness toward the far end of the scale until it clears
// `target` against `bg`, so an accent used as TEXT stays recognisably the
// theme's accent while still being legible. Light themes darken, dark themes
// lighten. Used for --ds-accent-ink, which is what accent-colored body copy
// and headings should use -- plain --ds-accent is tuned to sit on a surface
// as a fill, and on the light themes it reads at ~2.3:1 as text.
export function inkify(color, bg, target = 4.5) {
  const hsl = hexToHsl(color);
  if (!hsl) return color;
  if (contrast(color, bg) >= target) return color;
  const down = lum(bg) > 0.42;
  let best = color;
  for (let i = 1; i <= 100; i++) {
    const l = down ? hsl.l - i * 0.01 : hsl.l + i * 0.01;
    if (l < 0 || l > 1) break;
    best = hslToHex({ ...hsl, l });
    if (contrast(best, bg) >= target) return best;
  }
  return best;
}

// The surface a theme puts text on that is HARDEST for that text: the darkest
// one on a light theme, the lightest one on a dark theme. Contrast has to be
// checked against this, not against bgBase -- porcelain's bgSecondary is
// #fffdfb but its bgTertiary is #f3ebe3, and the muted scale only failed on
// the latter, which is exactly where the DM surfaces sit.
function hardestSurface(t) {
  const surfaces = [t.bgDeepest, t.bgBase, t.bgSecondary, t.bgTertiary, t.bgInput]
    .filter(c => typeof c === 'string' && c.startsWith('#') && c.length >= 7);
  if (!surfaces.length) return t.bgBase || '#000';
  return t.light
    ? surfaces.reduce((a, b) => (lum(b) < lum(a) ? b : a))
    : surfaces.reduce((a, b) => (lum(b) > lum(a) ? b : a));
}

export function buildTokens(t) {
  const isLight = !!t.light;
  const hardSurface = hardestSurface(t);
  // On a light ground, heavy black ambient shadows and neon accent glows (both
  // tuned for near-black canvases) read as dirty smudges. Soften the shadows to
  // gentle, cooler drops and dial the glows way down to a whisper.
  const shadow1 = isLight
    ? '0 1px 2px rgba(60,40,40,0.06), 0 8px 24px -14px rgba(60,40,40,0.18)'
    : '0 1px 2px rgba(0,0,0,0.45), 0 8px 24px -12px rgba(0,0,0,0.6)';
  const shadow2 = isLight
    ? '0 2px 6px rgba(60,40,40,0.08), 0 16px 40px -20px rgba(60,40,40,0.22)'
    : '0 2px 4px rgba(0,0,0,0.5), 0 16px 40px -16px rgba(0,0,0,0.75)';
  const shadow3 = isLight
    ? '0 4px 12px rgba(60,40,40,0.10), 0 28px 64px -24px rgba(60,40,40,0.26)'
    : '0 4px 8px rgba(0,0,0,0.5), 0 28px 64px -20px rgba(0,0,0,0.85)';
  const glowAccent = isLight ? `0 0 18px -8px ${wa(t.accentPrimary, '3a')}` : `0 0 24px -6px ${wa(t.accentPrimary, '73')}`;
  const glowGold   = isLight ? `0 0 16px -8px ${wa(t.accentGold, '33')}`   : `0 0 20px -4px ${wa(t.accentGold, '59')}`;
  const tk = {
    // ── surfaces (page depth 0 → raised 3, plus frosted glass) ──
    '--ds-surface-0': t.bgDeepest,
    '--ds-surface-1': t.bgBase,
    '--ds-surface-2': t.bgSecondary,
    '--ds-surface-3': t.bgTertiary,
    '--ds-input': t.bgInput,
    '--ds-hover': t.bgHover,
    '--ds-glass': wa(t.bgSecondary, isLight ? 'e6' : 'c4'),
    '--ds-glass-strong': wa(t.bgBase, isLight ? 'f2' : 'e3'),
    '--ds-glass-edge': wa(t.accentGold, isLight ? '55' : '3d'),
    // Interior sheen — a white lift on dark, a soft warm shade on light.
    '--ds-glass-light': isLight ? 'rgba(120, 90, 70, 0.045)' : 'rgba(255, 255, 255, 0.055)',
    // ── scrims ──
    // Small tinted plates that sit ON TOP of content: hover-action chips,
    // pills over artwork, floating controls. These were written all over the
    // app as literal rgba(0,0,0,0.4), which is a black smear on the light
    // themes and takes the white text sitting on it down with it. On a light
    // ground the same job needs a LIGHT plate with dark ink, so both the plate
    // and its ink flip together.
    '--ds-scrim': isLight ? 'rgba(255,255,255,0.78)' : 'rgba(0,0,0,0.40)',
    '--ds-scrim-strong': isLight ? 'rgba(255,255,255,0.92)' : 'rgba(0,0,0,0.65)',
    '--ds-on-scrim': isLight ? '#241712' : '#fff8f2',
    '--ds-mode': isLight ? 'light' : 'dark',
    // ── ink hierarchy ──
    '--ds-ink-hi': t.textPrimary,
    '--ds-ink': t.textSecondary,
    // Haley, 2026-10-02: "having trouble reading the text on the light colored
    // themes in dm". Measured across all 15 themes against hardestSurface():
    // ink-faint failed AA on 15/15 (worst 2.03:1, and 11 were under even 3:1)
    // and ink-low on 9/15. The palette above keeps the designer's intent; the
    // token layer walks each one's LIGHTNESS -- hue and saturation untouched --
    // until it actually reads. Body-weight muted text gets AA 4.5; faint is
    // only ever used for de-emphasised meta. 3:1 is the bare large-text floor,
    // but faint renders at 9-11px here (timestamps), which is exactly what
    // Haley said she could not read -- so it gets 3.5 to buy real legibility
    // while staying a visible step below ink-low, or the hierarchy collapses.
    '--ds-ink-low': inkify(t.textMuted, hardSurface, 4.5),
    '--ds-ink-faint': inkify(t.textFaint, hardSurface, 3.5),
    // ── lines ──
    '--ds-line-soft': t.borderDark,
    '--ds-line': t.borderMedium,
    '--ds-line-strong': t.borderLight,
    // ── accents ──
    '--ds-accent': t.accentPrimary,
    '--ds-accent-deep': t.accentPrimaryDark,
    // accent, but legible AS TEXT on this theme's lightest/darkest surface
    '--ds-accent-ink': inkify(t.accentPrimary, hardSurface, 4.5),
    '--ds-accent-deeper': shade(t.accentPrimary, 0.58),
    '--ds-accent-bg': t.accentPrimaryBg,
    '--ds-gold': t.accentGold,
    '--ds-gold-bright': t.accentSecondaryLight,
    '--ds-success': t.accentSuccess,
    '--ds-warning': t.accentWarning,
    '--ds-danger': t.accentDanger,
    '--ds-romance': t.accentRomance || t.accentPrimary,
    // ── on-colors: ink guaranteed readable on each accent surface ──
    '--ds-on-accent': readableOn(t.accentPrimary),
    '--ds-on-gold': readableOn(t.accentGold),
    '--ds-on-success': readableOn(t.accentSuccess),
    '--ds-on-romance': readableOn(t.accentRomance || t.accentPrimary),
    '--ds-success-deep': shade(t.accentSuccess, 0.35),
    '--ds-focus': wa(t.accentGold, 'cc'),
    // ── type ──
    '--ds-font-display': "'Cinzel', serif",
    '--ds-font-body': "'Cormorant Garamond', 'Georgia', serif",
    '--ds-font-ui': "'Inter', system-ui, sans-serif",
    '--ds-fs-xs': '11px',
    '--ds-fs-sm': '12.5px',
    '--ds-fs-base': '15.5px',
    '--ds-fs-md': '17px',
    '--ds-fs-lg': '19px',
    '--ds-fs-xl': '24px',
    '--ds-fs-2xl': '30px',
    '--ds-fs-3xl': '38px',
    // Reading rhythm (2026-09-14 refresh): one body line-height every
    // prose surface reads, so paragraphs in stories, DMs and channels sit
    // at the same measure.
    '--ds-lh-body': '1.62',
    '--ds-lh-tight': '1.3',
    // ── space (8-pt rhythm with a half step) ──
    '--ds-sp-1': '4px',
    '--ds-sp-2': '8px',
    '--ds-sp-3': '12px',
    '--ds-sp-4': '16px',
    '--ds-sp-5': '24px',
    '--ds-sp-6': '32px',
    '--ds-sp-7': '48px',
    '--ds-sp-8': '64px',
    // ── radius ──
    '--ds-r-sm': '10px',
    '--ds-r-md': '14px',
    '--ds-r-lg': '20px',
    '--ds-r-xl': '28px',
    '--ds-r-pill': '999px',
    // ── elevation (dark = candlelit black ambient + glow; light = soft drops) ──
    '--ds-shadow-1': shadow1,
    '--ds-shadow-2': shadow2,
    '--ds-shadow-3': shadow3,
    '--ds-glow-accent': glowAccent,
    '--ds-glow-gold': glowGold,
    // ── motion ──
    '--ds-dur-fast': '120ms',
    '--ds-dur-med': '240ms',
    '--ds-dur-slow': '480ms',
    '--ds-ease': 'cubic-bezier(0.22, 0.9, 0.3, 1)',
    '--ds-blur': '18px',
  };
  for (const a of ACCENT_ALPHA_STEPS) tk[`--ds-accent-a${a}`] = wa(t.accentPrimary, a);
  for (const a of GOLD_ALPHA_STEPS) tk[`--ds-gold-a${a}`] = wa(t.accentGold, a);
  return tk;
}

/**
 * Get the full theme object for a given theme ID
 * @param {string} themeId - Theme identifier
 * @returns {Object} Theme object with all color tokens
 */
// ─── ORNAMENT HARMONISATION ─────────────────────────────────────────────────
//
// accentGold is the ORNAMENT slot — borders, badges, dividers, the message
// bubble wash, every --ds-gold-* token. Every one of the 15 themes had picked a
// literal yellow-gold for it, including the blue ones, so on a blue theme every
// ornament rendered brown. Measured hue gap between each theme's own
// accentPrimary and its gold slot:
//
//   kya 155°   dark 168°   daybreak 168°   agnaragmund 166°   sebby 163°
//   atropos 160°   lachesis 134°   lance 97°   braxvaughn 88°   porcelain 61°
//
// Ten of fifteen more than 60° out — which is the "why is my blue theme brown"
// report. Rather than hand-guessing 15 palettes, the ornament is DERIVED: keep
// the metallic feel of the authored gold (its lightness and saturation, which
// is what makes it read as a precious-metal accent) but move it into the
// theme's own hue family, rotated +28° so it reads as a companion to the
// primary rather than a second copy of it.
//
// Themes whose gold is the POINT opt out with `goldIsIntentional: true`
// (kieran and alistair are literally gold-themed — their hue gap is already 0°).
function hexToHsl(hex) {
  const h = String(hex || '').replace('#', '').slice(0, 6);
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
  const l = (mx + mn) / 2;
  const d = mx - mn;
  if (!d) return { h: 0, s: 0, l };
  const s = l > 0.5 ? d / (2 - mx - mn) : d / (mx + mn);
  let hh;
  if (mx === r) hh = ((g - b) / d + (g < b ? 6 : 0));
  else if (mx === g) hh = (b - r) / d + 2;
  else hh = (r - g) / d + 4;
  return { h: (hh * 60 + 360) % 360, s, l };
}

function hslToHex({ h, s, l }) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  const seg = Math.floor(((h % 360) + 360) % 360 / 60);
  const [r, g, b] = [
    [c, x, 0], [x, c, 0], [0, c, x], [0, x, c], [x, 0, c], [c, 0, x],
  ][seg];
  const to = (v) => Math.round((v + m) * 255).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

export function harmoniseOrnament(t) {
  if (!t || t.goldIsIntentional) return t;
  const prim = hexToHsl(t.accentPrimary);
  const gold = hexToHsl(t.accentGold);
  if (!prim || !gold) return t;
  // Already in the primary's family — leave it be.
  const gap = Math.min(Math.abs(prim.h - gold.h), 360 - Math.abs(prim.h - gold.h));
  if (gap <= 60) return t;
  // Saturation is capped well below the primary's. A hue-rotated copy at the
  // gold's own saturation reads as a SECOND primary competing with the first;
  // what makes gold work as ornament is that it is a muted metal beside a
  // saturated accent. Capping at 0.40 and lifting lightness slightly gives
  // "brushed metal in this theme's colour" — pewter on the blues, rose-bronze
  // on the pinks — which sits behind the primary instead of fighting it.
  const harmonised = hslToHex({
    h: (prim.h + 28) % 360,
    s: Math.min(gold.s, 0.40),
    l: Math.min(0.72, gold.l + 0.06),
  });
  return { ...t, accentGold: harmonised, accentGoldOriginal: t.accentGold };
}

export const getTheme = (themeId) => {
  return harmoniseOrnament(THEMES[themeId] || THEMES.atropos);
};

/**
 * Get the color for a given species
 * Extracts species name from description strings like "Siren (Atlantic Royalty)"
 * @param {string} speciesString - Species description or name
 * @returns {string} Hex color code for the species
 */
export const getSpeciesColor = (speciesString) => {
  if (!speciesString) return '#d1b3ff';

  const speciesKeys = Object.keys(SPECIES_COLORS).sort((a, b) => b.length - a.length);

  for (const species of speciesKeys) {
    if (speciesString.includes(species)) {
      return SPECIES_COLORS[species];
    }
  }

  return '#d1b3ff';
};
